import Vue from 'vue'
import store from './store'
import router from './router'
import vuetify from './plugins/vuetify'
import keycloak from "@/plugins/keycloak"
import apiUrl from './data/apiUrl'
import axios from 'axios'
import qs from 'qs'
import App from './App.vue'
import './assets/scss/portal-style-new.scss'
import menu from "./data/menu.json"
import 'maplibre-gl/dist/maplibre-gl.css'
import CONST from './utils/const'
import { isCurrentAssignment as assignmentIsCurrent } from './utils/dateTime'

Vue.config.productionTip = false

/** User-visible text for axios failures; hides webpack proxy / socket noise in dev. */
function userFacingRequestErrorMessage (error) {
  const generic =
    `There was an error when processing your request. Please contact RealSAM support for further information.`

  const raw = error.response && error.response.data
  const asText = typeof raw === 'string'
    ? raw
    : (raw && typeof raw.message === 'string' ? raw.message : '')

  if (!error.response) {
    return generic
  }
  if (asText && /proxy error|ECONNRESET|could not proxy/i.test(asText)) {
    return generic
  }
  if (
    (error.response.status === 502 || error.response.status === 504) &&
    (!asText || /proxy error|Bad Gateway|ECONNRESET|could not proxy/i.test(asText))
  ) {
    return generic
  }
  if (typeof raw === 'string' && raw.trim()) return raw
  if (raw && typeof raw.message === 'string' && raw.message.trim()) return raw.message
  return generic
}

// AXIOS
Vue.use({
  install (Vue) {
    Vue.prototype.$api = axios.create({
      baseURL:  process.env.NODE_ENV === 'production' 
        ? process.env.VUE_APP_API 
        : '',
      headers: {'x-requested-with': 'XMLHttpRequest'}
    })
  }
})

// KEYCLOAK OIDC
Vue.use(keycloak)

// VUE MAIN
Vue.$keycloak
  .init({ 
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html'
  })
  .then(() => {
    new Vue({
      data: {
        apiUrl: apiUrl,
        appVersion: process.env.VUE_APP_VER,
        authTimer: null
      },
      store,
      router,
      apiUrl,
      vuetify,
      render: h => h(App),
      created() {
        // Setting up custom logger
        if (process.env.NODE_ENV === 'development') {
          this.logger = console.log.bind(window.console)
        } else {
          this.logger = function() {}
        }
      },
      mounted: function () {
        // Start the session idle timer
        this.keycloakSessionIdleTimer()
      },
      methods: {
        keycloakSessionIdleTimer() {
          // This method is used to set timer to check if the keycloak session
          // idle is expired.
          this.logger("Session started...")
          this.authTimer = setInterval(() => {
            this.logger("Check session...")
            this.checkAccessTokenExpiration(null, true)
          }, 
          30 * 60 * 1000 + 5000
          ) // Session idle value: 30 minutes 5 seconds
        },
        checkAccessTokenExpiration(callback=null, sessionIdleExpired=false) {
          // Check if the token needs to be refreshed.
          // If the token will be expired within 10 seconds, then refresh it.
          Vue.$keycloak.updateToken(10)
            .then((refreshed) => {
              if (refreshed) {
                this.logger("Token is refreshed...")
                // If the token is refreshed, then the timer is restarted
                // because Keycloak session idle is also resetted.
                clearInterval(this.authTimer)
                this.keycloakSessionIdleTimer()
              }
              
              if (callback) {
                callback()
              }
              
            })
            .catch(error => {
              this.logger(error)
              // When the SSO session is in idle state and 
              // the user is still authenticated, logout the user.
              // Checking if authed is important for users that click the logout button
              if (this.$keycloak.authenticated) this.$keycloak.logout()
              
              // If the request comes from the keycloakSessionIdleTimer method
              // then logout the user
              if (sessionIdleExpired) {
                this.$root.$emit('popupMessage', 'sessionExpired')
              }
            })
        },
        buildUserMenu() {
          // console.log(this.$root.apiUrl.getUsers)
          // console.log(this.$root.apiUrl.getUserDetail.replace("$userId", "10"))
          this.$store.commit("setUserDataLoadError", null)
          this.logger(this.$keycloak.token)
          // Get User Permission
          let params = new URLSearchParams()
          this.$root.axiosRequest("get", this.$root.apiUrl.getMeUser, params, 
            (response) => {
              // On success.
              this.$store.commit("setUserData", response.data)

              let userPermissions = []
              if (response.status === 200) {
                userPermissions = response.data.permissions
              }

              let filteredMenu = this.filterMenu(userPermissions)
              this.$store.commit("setUserMenu", filteredMenu)

              const configs = response.data.organisation.configs
              let portalConfig = null
              if (Array.isArray(configs)) {
                portalConfig = configs.find((c) => c && c.portalType === "management")
              }
              if (portalConfig) {
                this.setOrgConfig(portalConfig)
              }
            }, 
            (error) => {
              let filteredMenu = this.filterMenu([])
              this.$store.commit("setUserMenu", filteredMenu)
              this.$store.commit("setUserDataLoadError", error.userFacingMessage)
            },
            () => {
              // Finally
            })
        },
        filterMenu(userPermissions) {
          let filteredMenu = []
    
          // Loop over menu categories
          // menu var is imported from ./data/menu.json
          menu.forEach(m => {
            let newMenu = {}
            newMenu.order = m.order
            newMenu.name = m.name
            newMenu.title = m.title
            newMenu.subtitle = m.subtitle
            newMenu.slug = m.slug
            newMenu.icon = m.icon
            newMenu.isHidden = m.isHidden // Hide menu from the sidebar
            newMenu.submenu = []

            // Only for Dashboard
            // if (m.name === 'Dashboard') {
            //   filteredMenu.push(newMenu)
            //   return
            // }
    
            // Loop over the submenu where the object contains required permission
            m.submenu.forEach(sm => {
              let is_permitted = true
    
              // Loop over the list of permission of the submenu
              for (let p of sm.permissions) {
    
                // Check if the permission of the submenu is in the userPermission
                // If not then user is not allowed to see this submenu
                if (userPermissions.indexOf(p) === -1) {
                  is_permitted = false
                  break
                }
              }
    
              if (is_permitted === true) {
                newMenu.submenu.push(sm)
              }
            })
    
            if (newMenu.submenu.length > 0) {
    
              // Sort submenu based on submenu title
              newMenu.submenu.sort(function(a, b) {
                let titleA = a.title.toUpperCase()
                let titleB = b.title.toUpperCase()
                return (titleA < titleB) ? -1 : (titleA > titleB) ? 1 : 0
              })

              filteredMenu.push(newMenu)
            }
          })

          filteredMenu.sort(function(a, b) {
            return (a.order < b.order) ? -1 : (a.order > b.order) ? 1 : 0
          })
    
          return filteredMenu
        },
        getSubmenu(menuName) {
          let menuList = this.$store.state.userMenu

          for (let menu of menuList) {
            if (menu.name === menuName) {
              return menu.submenu
            }
          }
          return []
        },
        formatDate(date, formatType=null) {
          if (!date) return "-"
          // Input is in the format 2022-05-10 00:00:00.0
          let newDate = new Date(date)
          if (formatType==='sql') {
            let month = newDate.getMonth() + 1
            return [newDate.getFullYear(), "-",
              (month > 9 ? '' : '0') + month, "-",
              (newDate.getDate() > 9 ? '' : '0') + newDate.getDate()
             ].join('');
          }
          let month = ""
          switch (newDate.getMonth()) {
            case 0:
              month = "Jan"
              break
            case 1:
              month = "Feb"
              break
            case 2:
              month = "Mar"
              break
            case 3:
              month = "Apr"
              break
            case 4:
              month = "May"
              break
            case 5:
              month = "Jun"
              break
            case 6:
              month = "Jul"
              break
            case 7:
              month = "Aug"
              break
            case 8:
              month = "Sep"
              break
            case 9:
              month = "Oct"
              break
            case 10:
              month = "Nov"
              break
            case 11:
              month = "Dec"
              break
          }
          return String(newDate.getDate()).padStart(2, '0') + " " + month + " " + newDate.getFullYear()
        },
        formatProductPrice(currency, price) {
          const formatted = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'code',
          }).format(price / 100);
          return formatted
        },
        formatDateTime(datetime, formatType=null) {
          if (!datetime) return "-"
          let time = datetime.split(" ")[1]
          return this.formatDate(datetime, formatType) + " " + time
        },
        formatDateToLocal(dateStr) {
          if (!dateStr) return "-"

          const hasTime = dateStr.includes(' ');
          const normalized = hasTime
            ? dateStr.replace(' ', 'T') + 'Z'
            : dateStr + 'T00:00:00Z';

          const date = new Date(normalized);

          const day = String(date.getDate()).padStart(2, '0');
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const month = months[date.getMonth()];
          const year = date.getFullYear();

          return `${day} ${month} ${year}`;
        },
        isCurrentAssignment(item, now) {
          return assignmentIsCurrent(item, now)
        },
        formatDateTimeToLocal(datetime) {
          if (!datetime) return "-"
          
          datetime = datetime.replace(' ', 'T') + 'Z'
          const date = new Date(datetime);

          const day = String(date.getDate()).padStart(2, '0');
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const month = months[date.getMonth()];
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');

          return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
        },
        formatNullText(text) {
          if (text === null || text === 'null' || text === '' || text === undefined) {
            return '-'
          }
          return text
        },
        getSupportStatusColor(status) {
          if (status === CONST.DEVICE_SUPPORT_STATUS_SUPPORTED) return "success darken-2"
          if (status === CONST.DEVICE_SUPPORT_STATUS_NEARING_END) return "yellow darken-2"
          if (status === CONST.DEVICE_SUPPORT_STATUS_CRITICAL_ONLY) return "warning darken-2"
          if (status=== CONST.DEVICE_SUPPORT_STATUS_UNSUPPORTED) return "error darken-2"
        },
        selectValuesToString(values) {
          let text = ""
          if (values.length === 0) return "-"

          if (values) {
            values.forEach(val => {
              if (typeof val === 'object') {
                if (val.name) text += val.name + ", "
                else text += "-, "
              }
            })
            text = text.substring(0, text.length - 2);
            return text
          }
          
          
        },
        addressStringBuilder(addressObj) {
          // If the address id is 0, it means the user has null value for address
          if (addressObj === null || addressObj.id === 0) return "-"

          let addressArr = []
          if (addressObj.line1 !== "") addressArr.push(addressObj.line1)
          if (addressObj.line2 !== "") addressArr.push(addressObj.line2)

          // Postal code is attached to state if it exists otherwise it is attached to city 
          if (addressObj.state !== null && addressObj.state.id !== 0) {
            if (addressObj.city !== "") 
              addressArr.push(addressObj.city)

            addressArr.push(addressObj.state.name + " " + addressObj.postalCode)
          } else {
            if (addressObj.city !== "") 
              addressArr.push(addressObj.city + " " + addressObj.postalCode)
          }

          if (addressObj.country !== null) 
            addressArr.push(addressObj.country.name)
          
          if (addressArr.length === 0) return "-"

          return addressArr.join(", ")
        },
        cleanFormData(formData, finalFormData) {
          // Remove key for form data.
          // Fyi, there is case where formData doesn't have the key but it 
          // exist in finalFormData, i.e. userId, addressId
          // formData is only for reviewing the data so it mainly contain text
          // finalFormData is the one that will be sent to backend
          Object.keys(formData).forEach((k) => {
            if (formData[k] === "" || formData[k] == null
              || finalFormData[k] === "" || finalFormData[k] == null) {
              delete formData[k]
              delete finalFormData[k]
            }
          })
        },
        isUserDistributor() {
          if (this.$store.state.userData.roles.length === 1 
              && this.$store.state.userData.roles[0].name === 'distributor') {
            return true
          }

          return false
        },
        isUserHasPermissions(permissions) {
          for (let e of permissions) {
            if (!this.isUserHasPermission(e)) return false
          }
          return true
        },
        isUserHasPermission(permission) {
          if (this.$store.state.userData.permissions.indexOf(permission) > -1)
            return true
          
            return false
        },
        isUserHasOneOfPermissions(permissions) {
          for (let e of permissions) {
            if (this.isUserHasPermission(e)) return true
          }
          return false
        },
        createTemporaryPassword(upperLen=3, lowerLen=3, numLen=2) {
          let result = '';
          let upperCaseAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
          let lowerCaseAlphabet = 'abcdefghijklmnopqrstuvwxyz'
          let numeric = '0123456789'
          for ( let i = 0; i < upperLen; i++ ) {
            result += upperCaseAlphabet.charAt(Math.floor(Math.random() * upperCaseAlphabet.length));
          }
          for ( let i = 0; i < lowerLen; i++ ) {
            result += lowerCaseAlphabet.charAt(Math.floor(Math.random() * lowerCaseAlphabet.length));
          }
          for ( let i = 0; i < numLen; i++ ) {
            result += numeric.charAt(Math.floor(Math.random() * numeric.length));
          }
          return result;
        },
        setOrgConfig(config, mode) {
          let colors = {
            primary: config.primaryColor,
            secondary: config.secondaryColor,
            accent: config.accentColor,
          }
          this.changeTheme(colors)
          this.changeFavicon(config.faviconUrl)
          this.$store.commit("setOrgConfig", config)
        },
        changeTheme(colors) {
          if (colors.primary) {
            this.$vuetify.theme.themes.light.primary = colors.primary
            this.$vuetify.theme.themes.dark.primary = colors.primary
          }
            
          if (colors.secondary) {
            this.$vuetify.theme.themes.light.secondary = colors.secondary
            this.$vuetify.theme.themes.dark.secondary = colors.secondary
          }

          if (colors.accent) {
            this.$vuetify.theme.themes.light.accent = colors.accent
            this.$vuetify.theme.themes.dark.accent = colors.accent
          }
            
        },
        changeFavicon(faviconUrl) {
          if (faviconUrl) {
            let link = document.querySelector("link[rel~='icon']")
            if (!link) {
                link = document.createElement('link')
                link.rel = 'icon'
                document.head.appendChild(link)
            }
            link.href = faviconUrl
          }
        },
        setLocalStorage(key, val) {
          localStorage.setItem(key, val)
        },
        getLocalStorage(key) {
          return localStorage.getItem(key)
        },
        isDarkTheme() {
          return this.getLocalStorage("isDarkTheme") === "true"
        },
        loadTableOptionsFromQueryParams(options) {
          let queryParams = new URLSearchParams(window.location.search)
          if (!queryParams.has("page")) {
            return
          }
          
          const page = queryParams.get("page") || 1
          const itemsPerPage = queryParams.get("itemsPerPage") || options.itemsPerPage

          Object.assign(options, {
            page: parseInt(page),
            itemsPerPage: parseInt(itemsPerPage),
            sortBy: queryParams.getAll("sortBy"),
            sortDesc: queryParams.getAll("sortDesc")
          })
        },
        axiosRequest(method, url, params, successCallback, errorCallback, finallyCallback){
          
          this.checkAccessTokenExpiration(() => {
            let config = {
              headers: { Authorization: "Bearer " + Vue.$keycloak.token },
              method: method,
              url: url,
            }
      
            if (method === "post" || method === "put" || method === "delete") {
              // config.data must be an instance of URLSearchParams
              config.data = params
              if (params instanceof FormData) {
                config.headers["Content-Type"] = "multipart/form-data"
              }
            } else if (method === "get") {
              config.params = params
              config.paramsSerializer = (params) => {
                if (params instanceof URLSearchParams) return params
                return qs.stringify(params, { arrayFormat: 'comma' })
              }
            }
                      
            // Run axios request
            this.$api.request(config)
              .then(response => {
                this.logger(url)
                this.logger(response)
                successCallback(response)
              })
              .catch(error => {
                this.logger(url)
                this.logger(error)

                error.userFacingMessage = userFacingRequestErrorMessage(error)

                // Request to re-login if the error is 401
                if (error.response) {
                  if (error.response.data) {
                    if (/401: Unauthorized/.test(error.response.data)) {
                      this.$keycloak.login({
                        redirectUri: window.location.href
                      })
                    } 
                  }
                }
                errorCallback(error)
              })
              .finally(() => {
                finallyCallback()
              })
          })
          
        },
      }
    }).$mount('#app')
  })

